// ============================================================================
// Ciclo de desarrollo (#559) — tarjeta de la VISTA INTERNA de la ficha.
// ============================================================================
// Línea de tiempo + «Mover a» + enlaces técnicos + bitácora de quién movió qué.
// La mayoría de las etapas se mueven solas con eventos reales (issue
// vinculado, rama, PR, deploy a QA); el selector es la corrección humana y la
// única vía práctica para «Validado por staff» y «Rechazado en QA». La
// confirmación y la llamada al API las hace la sección (onChangeStage).
// ============================================================================
import { useTranslation } from "react-i18next";
import { formatDate } from "../services/serviceDeskService";
import {
  hasStageLinks,
  safeHref,
  selectableStages,
  type DevStage,
  type DevStageId,
  type StageOption,
} from "../services/devStages";
import { StageBadge, StageTimeline } from "./StageTimeline";

interface Props {
  devStage?: DevStage | null;
  settings?: { stages?: StageOption[] } | null;
  isDarkMode: boolean;
  saving?: boolean;
  onChangeStage: (stage: DevStageId) => void;
}

export function DevStagePanel({ devStage, settings, isDarkMode, saving = false, onChangeStage }: Props) {
  const { t } = useTranslation("serviceDesk");
  const card = isDarkMode ? "bg-dark-surface border-dark-border" : "bg-white border-gray-200";
  const text = isDarkMode ? "text-white" : "text-gray-900";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const border = isDarkMode ? "border-dark-border" : "border-gray-200";
  const selCls = `w-full rounded-lg border px-3 py-1.5 text-sm ${
    isDarkMode ? "!bg-gray-800 !border-gray-600 !text-white" : "!bg-white !border-gray-300"
  }`;
  const links = devStage?.links;
  const history = devStage?.history || [];
  const issueHref = safeHref(links?.issueUrl);
  const prHref = safeHref(links?.prUrl);
  const qaHref = safeHref(links?.qaUrl);

  return (
    <div className={`rounded-xl border p-5 ${card}`} data-testid="dev-stage-panel">
      <div className="mb-3 flex items-center justify-between">
        <h2 className={`text-sm font-semibold ${text}`}>{t("ticketDetail.stage.title")}</h2>
        <StageBadge devStage={devStage} />
      </div>

      {devStage?.timeline?.length ? (
        <StageTimeline steps={devStage.timeline} currentLabel={devStage.label} compact isDarkMode={isDarkMode} />
      ) : (
        <p className={`text-xs ${muted}`}>{t("ticketDetail.stage.empty")}</p>
      )}

      <div className="mb-3 mt-3">
        <label className={`mb-1 block text-xs font-medium ${muted}`} htmlFor="sd-dev-stage">
          {t("ticketDetail.stage.moveTo")}
        </label>
        <select
          id="sd-dev-stage"
          className={selCls}
          value={devStage?.current || "received"}
          onChange={(e) => onChangeStage(e.target.value as DevStageId)}
          disabled={saving}
        >
          {selectableStages(settings).map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
        <p className={`mt-1 text-[11px] ${muted}`}>{t("ticketDetail.stage.help")}</p>
      </div>

      {hasStageLinks(links) && (
        <div className={`border-t pt-3 ${border}`}>
          <span className={`mb-1.5 block text-xs font-medium ${muted}`}>{t("ticketDetail.stage.linksTitle")}</span>
          <div className="flex flex-col gap-1.5">
            {issueHref && (
              <a href={issueHref} target="_blank" rel="noreferrer" className="text-xs font-medium text-blue-500 underline">
                {t("ticketDetail.stage.issue")}
              </a>
            )}
            {prHref && (
              <a href={prHref} target="_blank" rel="noreferrer" className="text-xs font-medium text-blue-500 underline">
                {t("ticketDetail.stage.pr")}
              </a>
            )}
            {qaHref && (
              <a href={qaHref} target="_blank" rel="noreferrer" className="text-xs font-medium text-blue-500 underline">
                {t("ticketDetail.stage.qa")}
              </a>
            )}
            {links?.branch && (
              <span className={`truncate font-mono text-[11px] ${muted}`} title={links.branch}>
                {t("ticketDetail.stage.branch", { value: links.branch })}
              </span>
            )}
            {links?.repo && (
              <span className={`font-mono text-[11px] ${muted}`}>{t("ticketDetail.stage.repo", { value: links.repo })}</span>
            )}
          </div>
        </div>
      )}

      {history.length > 0 && (
        <details className={`mt-3 border-t pt-3 ${border}`}>
          <summary className={`cursor-pointer text-xs font-medium ${muted}`}>
            {t("ticketDetail.stage.history", { count: history.length })}
          </summary>
          <ul className="mt-2 space-y-1.5">
            {[...history].reverse().map((h, i) => (
              <li key={`${h.stage}-${h.at ?? i}-${i}`} className={`text-[11px] ${muted}`}>
                <strong className={text}>{h.label}</strong> ·{" "}
                {h.actorName || t(`ticketDetail.stage.sources.${h.source || "system"}`)} ·{" "}
                {formatDate(h.at ?? null)}
                {h.note ? <span className="block opacity-80">{h.note}</span> : null}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
